import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import {
  getAuctionEventById,
  getAllBiddersForEvent,
  getAllBidsForBanker,
  getAllWrongBidsForBanker,
  getAllEventAnnouncements,
} from "services";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";
import LogoSvg from "../../Icons/LogoSvg";

export default function ViewEventReport(props: any) {
  const router = useRouter();
  const { id } = router.query;
  const [event, setEvent] = useState<any>([]);
  const [bidders, setBidders] = useState<any>([]);
  const [bidHistory, setBidHistory] = useState<any>([]);
  const [announcements, setAnnouncements] = useState<any>([]);
  const [wrongBidHistory, setWrongBidHistory] = useState<any>([]);
  const [nonParticiatedBidders, setNonParticiatedBidders] = useState<any>([]);
  const [biddersLoggedHistory, setBiddersLoggedHistory] = useState<any>([]);

  useEffect(() => {
    if (id) {
      init(id);
    }
  }, [id]);

  const findNonParticiatedBidder = (
    bidders: any,
    allBids: any,
    allWrongBids: any
  ) => {
    return bidders.filter((bidder: any) => {
      const user_uuid = bidder.user_uuid;
      const userExistsInCorrectBids = allBids.some(
        (bid: any) => bid.user_uuid === user_uuid
      );
      const userExistsInWrongBids = allWrongBids.some(
        (wrongBid: any) => wrongBid.user_uuid === user_uuid
      );

      return !userExistsInCorrectBids && !userExistsInWrongBids;
    });
  };

  function getLoggedHistory(data: any) {
    const uniqueEmails = data.reduce((newArray: any, obj: any) => {
      const { user_uuid, created_at } = obj;
      const existingObj = newArray.find(
        (item: any) => item.user_uuid === user_uuid
      );

      if (existingObj) {
        if (created_at > existingObj.created_at) {
          return newArray.map((item: any) =>
            item.user_uuid === user_uuid ? obj : item
          );
        }
      } else {
        return [...newArray, obj];
      }

      return newArray;
    }, []);

    return uniqueEmails;
  }

  const init = async (id: any) => {
    const data: any = await getAuctionEventById(id);
    setEvent(data);
    if (data) {
      const allBidders: any = await getAllBiddersForEvent(data.uuid);
      const bidders = allBidders.filter(
        (bidder: any) => bidder.status === "verified"
      );
      setBidders(bidders);
      const bidderNameMap = new Map<string, string>();
      bidders?.forEach((bidder: any) => {
        bidderNameMap.set(bidder.user_uuid, bidder?.meta?.name);
      });
      const allBids: any = await getAllBidsForBanker(data.uuid);
      const allBidsWithNames = allBids?.map((bid: any) => ({
        ...bid,
        bidder_name: bidderNameMap?.get(bid.user_uuid) || "-",
      }));
      setBidHistory(allBidsWithNames);
      const allWrongBids: any = await getAllWrongBidsForBanker(data.uuid);
      const allWrongBidsWithNames = allWrongBids?.map((bid: any) => ({
        ...bid,
        bidder_name: bidderNameMap?.get(bid.user_uuid) || "-",
      }));
      setWrongBidHistory(allWrongBidsWithNames);
      const nonData: any = findNonParticiatedBidder(
        bidders,
        allBids,
        allWrongBids
      );
      setNonParticiatedBidders(nonData);
      const loggedData = getLoggedHistory(
        allBidsWithNames?.concat(allWrongBidsWithNames)
      );
      setBiddersLoggedHistory(loggedData);
      const eventAnnouncements: any = await getAllEventAnnouncements(data.uuid);
      if (eventAnnouncements?.data) {
        setAnnouncements(eventAnnouncements.data);
      }
    }
  };
  return (
    <div className="flex flex-col">
      <Header />
      <div className="bg-white pt-16 dark:bg-[#12263f] flex flex-col px-20">
        <PropertyDetail event={event} />
        <EventDetail event={event} />
        <BidderDetail event={event} bidders={bidders} />
        <BidsHistoryDetail
          event={event}
          bidHistory={bidHistory}
          wrongBidHistory={wrongBidHistory}
        />
        <WinnerDetail
          event={event}
          bidHistory={bidHistory}
          wrongBidHistory={wrongBidHistory}
          nonParticiatedBidders={nonParticiatedBidders}
          biddersLoggedHistory={biddersLoggedHistory}
        />
        <AnnouncementDetails announcementHistory={announcements} />
        <Footer />
      </div>
    </div>
  );
}

const PropertyDetail = (props: any) => {
  return (
    <div>
      <div className="text-2xl text-center font-semibold mb-4">
        Report for e-Auction of Property by {props.event?.bank?.name},{" "}
        {props.event?.branch?.name} Branch Borrower -{" "}
        {props.event?.borrower_name} Reserve Price -{" "}
        {props.event?.reserve_price}/-
      </div>
      <div className="text-lg text-left mb-4">
        {`Basic Details: The table below gives you insights into the event's
        property details and bank details.`}
      </div>
      <div>
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50 w-full">
            <tr>
              <th
                scope="col"
                className="w-1/3 py-3.5 pl-4 pr-3 text-sm border border-black font-semibold text-gray-900 sm:pl-6"
              >
                Header
              </th>
              <th
                scope="col"
                className="2/3 px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Particulars
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white max-h-80 overflow-auto">
            <tr className="">
              <td className="whitespace-nowrap py-4 pl-4 text-center border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Bank Name
              </td>
              <td className=" capitalize text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.bank?.name ? props.event?.bank?.name : "-"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Branch Name
              </td>
              <td className=" capitalize text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.branch?.name ? props.event?.branch?.name : "-"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Event Title
              </td>
              <td className=" capitalize text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.event_name ? props.event?.event_name : "-"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Listing ID
              </td>
              <td className=" capitalize text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.listing_id ? props.event?.listing_id : "-"}
              </td>
            </tr>
            {/* <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                AO Name
              </td>
              <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.meta?.assigned_ao?.email
                  ? props.event?.meta?.assigned_ao?.email
                  : "-"}
              </td>
            </tr> */}
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Approver Name
              </td>
              <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.meta?.approver[0]?.name
                  ? props.event?.meta?.approver[0]?.name
                  : props.event?.meta?.approver[0]?.email
                  ? props.event?.meta?.approver[0]?.email
                  : "-"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Property Details
              </td>
              <td className="text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.property_dump?.address
                  ? props.event?.property_dump?.address
                  : "-"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Borrower Details
              </td>
              <td className=" text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.borrower_name ? props.event?.borrower_name : "-"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EventDetail = (props: any) => {
  return (
    <div className="my-8">
      <div className="text-lg text-left mb-4">
        Complete information on timelines scheduled and actual timelines,
        extension rules, and other auction financial information is contained in
        this section.
      </div>
      <div>
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50 w-full">
            <tr>
              <th
                scope="col"
                className="w-1/3 py-3.5 pl-4 pr-3 text-sm border border-black font-semibold text-gray-900 sm:pl-6"
              >
                Header
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Particulars
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white max-h-80 overflow-auto">
            <tr className="">
              <td className="whitespace-nowrap py-4 pl-4 text-center border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Date of E-Auction
              </td>
              <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.start_date_time
                  ? DateTime.fromISO(props.event?.start_date_time)
                      .setZone("Asia/Kolkata")
                      .toFormat("DD")
                  : "00"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Scheduled Start Time
              </td>
              <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.start_date_time
                  ? `${DateTime.fromISO(props.event?.start_date_time)
                      .setZone("Asia/Kolkata")
                      .toFormat("hh:mm:ss a")} (IST)`
                  : "00"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Scheduled End Time
              </td>
              <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.end_date_time
                  ? `${DateTime.fromISO(props.event?.end_date_time)
                      .setZone("Asia/Kolkata")
                      .toFormat("hh:mm:ss a")} (IST)`
                  : "00"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Actual Start Time
              </td>
              <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.start_date_time
                  ? `${DateTime.fromISO(props.event?.start_date_time)
                      .setZone("Asia/Kolkata")
                      .toFormat("hh:mm:ss a")} (IST)`
                  : "00"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Actual End Time
              </td>
              <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.final_end_date
                  ? `${DateTime.fromMillis(
                      parseInt(props.event?.final_end_date)
                    )
                      .setZone("Asia/Kolkata")
                      .toFormat("hh:mm:ss a")} (IST)`
                  : "00"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                No Of Extensions
              </td>
              <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.number_of_extension
                  ? props.event?.number_of_extension
                  : "-"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Auto Extension Type
              </td>
              <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.auto_extension_type
                  ? props.event?.auto_extension_type
                  : "-"}
              </td>
            </tr>
            {props.event?.auto_extension_limit &&
              props.event?.auto_extension_limit !== "Unlimited" && (
                <tr className="">
                  <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                    Auto Extension Limit
                  </td>
                  <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                    {props.event?.auto_extension_limit}
                  </td>
                </tr>
              )}
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Auto Extension @
              </td>
              <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.extend_time_at
                  ? props.event?.extend_time_at
                  : "-"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Auto Extension Period
              </td>
              <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.extend_time_by
                  ? props.event?.extend_time_by
                  : "-"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Auction Currency
              </td>
              <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.auction_currency
                  ? props.event?.auction_currency
                  : "-"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Reserve Price
              </td>
              <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.reserve_price ? props.event?.reserve_price : "-"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                EMD Amount
              </td>
              <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.property_dump?.emd_amount
                  ? props.event?.property_dump?.emd_amount
                  : "-"}
              </td>
            </tr>
            <tr className="">
              <td className="whitespace-nowrap text-center py-4 pl-4 border border-black pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                Minimum Increment Amount/ Bid Multiple Amount
              </td>
              <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                {props.event?.bidding_increment_amount
                  ? props.event?.bidding_increment_amount
                  : "-"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="text-lg font-semibold mt-12">
        <div>EMD Remittance Account Details:</div>
        <div className="flex justify-between">
          <div>
            <div>
              EMD Account Details: {props.event?.emd_payment_account_number}
            </div>
            <div>Name of Account: {props.event?.emd_payment_account_name}</div>
          </div>
          <div>
            <div>Bank Name: {props.event?.emd_payment_account_bank}</div>
            <div>IFSC Code: {props.event?.emd_payment_account_ifsc}</div>
          </div>
          <div>
            <div>Branch Name: {props.event?.emd_payment_account_branch}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BidderDetail = (props: any) => {
  return (
    <div>
      <div className="text-2xl font-semibold mb-4">Bidder Details</div>
      <div className="text-lg text-left mb-4">
        Bidders who are allowed to participate in the auction and their
        eligibility details are mentioned here, along with the approver from the
        bank side who authorized these bidders. Information is available below
        the report.
      </div>
      <div>
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50 w-full">
            <tr>
              <th
                scope="col"
                className="w-1/7 py-3.5 pl-4 pr-3 text-sm border border-black font-semibold text-gray-900 sm:pl-6"
              >
                S No
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Bidder Name
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Bidder Email Id
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                EMD
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                KYC
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Application
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                TNC Done
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Approved By
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white max-h-80 overflow-auto">
            {props.bidders &&
              props.bidders.map((item: any, index: number) => {
                return (
                  <tr className="" key={index}>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {index + 1}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item.meta?.name ? item.meta?.name : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item.email ? item.email : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item.emd_amount_paid
                        ? item.emd_amount_paid
                          ? item?.created_by === "user"
                            ? "YES-Online"
                            : "YES-Offline"
                          : "No"
                        : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item.kyc_verified
                        ? item.kyc_verified
                          ? item?.created_by === "user"
                            ? "YES-Online"
                            : "Yes-Offline"
                          : "No"
                        : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item.application_form_filled
                        ? item.application_form_filled
                          ? item?.created_by === "user"
                            ? "YES-Online"
                            : "Yes-Offline"
                          : "No"
                        : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item.agreement_signed
                        ? item.agreement_signed
                          ? item?.created_by === "user"
                            ? "YES-Online"
                            : "Yes-Offline"
                          : "No"
                        : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item.meta?.approver_name
                        ? item.meta?.approver_name
                        : item.meta?.approver_email
                        ? item.meta?.approver_email
                        : "-"}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="mt-8">
        <div>
          <span className="font-semibold">KYC:</span>{" "}
        </div>
        <div>
          {" "}
          <span className="font-semibold">Aadhaar:</span> Verified through
          E-Aadhaar{" "}
        </div>
        <div>
          {" "}
          <span className="font-semibold">PAN:</span> Validated through Tin-NSDL
        </div>
        <div>
          {" "}
          <span className="font-semibold">Offline:</span> Collected Offline by
          Bank{" "}
        </div>
        <div>
          {" "}
          <span className="font-semibold">EMD:</span>{" "}
        </div>
        <div>
          <span className="font-semibold">RTGS/NEFT / Offline:</span> Remitted
          Online through RTGS/NEFT/FT Directly{" "}
        </div>
        <div>
          {" "}
          <span className="font-semibold">Gateway:</span> Gateway Facilitated by
          Auctionbazaar.com{" "}
        </div>
        <div>
          <span className="font-semibold">DD/PO / Offline:</span> Submitted to
          Bank
        </div>
        <div>
          {" "}
          <span className="font-semibold">Document Sign:</span>(TNC and
          Application Form){" "}
        </div>
        <div>
          {" "}
          <span className="font-semibold">Offline:</span> Signed form Submitted
          at Bank{" "}
        </div>
        <div>
          {" "}
          <span className="font-semibold">DSC:</span> Signed using Digital
          Signature Key{" "}
        </div>
        <div>Aadhaar OTP Based: OTP Based Sign done On Auctionbazaar.com</div>
      </div>
    </div>
  );
};

const BidsHistoryDetail = (props: any) => {
  return (
    <div className="my-8">
      <div className="text-base text-left mb-4">
        Complete Audit trail of Bids placed by bidders and Valid and Invalid
        bids for the auction are listed here for banks/nbfc records:
      </div>
      <div>
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50 w-full">
            <tr>
              <th
                scope="col"
                colSpan={6}
                className=" w-full table-cell py-3.5 pl-4 pr-3 text-sm border border-black font-semibold text-gray-900 sm:pl-6"
              >
                Bids History
              </th>
            </tr>
            <tr>
              <th
                scope="col"
                className="py-3.5 pl-4 pr-3 text-sm border border-black font-semibold text-gray-900 sm:pl-6"
              >
                Index
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Bidder Name
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Email
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Bid
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Time
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                IP Address
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white max-h-80 overflow-auto">
            {props.bidHistory &&
              props.bidHistory.reverse().map((item: any, index: number) => {
                return (
                  <tr className="" key={index}>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {index + 1}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item?.bidder_name ? item?.bidder_name : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item?.meta?.email ? item?.meta?.email : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item?.bidding_amount ? item?.bidding_amount : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {/* {item?.created_at
                        ? DateTime.fromISO(item?.created_at).toFormat(
                            "hh:mm:ss a"
                          )
                        : "-"} */}
                      {item?.bid_timestamp
                        ? `${DateTime.fromMillis(parseInt(item?.bid_timestamp))
                            .setZone("Asia/Kolkata")
                            .toFormat("hh:mm:ss a")} (IST)`
                        : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item?.meta?.user_ip ? item?.meta?.user_ip : "-"}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      <div className="mt-8">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50 w-full">
            <tr>
              <th
                scope="col"
                colSpan={7}
                className=" w-full table-cell py-3.5 pl-4 pr-3 text-sm border border-black font-semibold text-gray-900 sm:pl-6"
              >
                Invalid Bids History
              </th>
            </tr>
            <tr>
              <th
                scope="col"
                className="py-3.5 pl-4 pr-3 text-sm border border-black font-semibold text-gray-900 sm:pl-6"
              >
                Index
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Bidder Name
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Email
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Bid
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Time
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                IP Address
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Reason
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white max-h-80 overflow-auto">
            {props.wrongBidHistory &&
              props.wrongBidHistory
                .reverse()
                .map((item: any, index: number) => {
                  return (
                    <tr className="" key={index}>
                      <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                        {index + 1}
                      </td>
                      <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                        {item?.bidder_name ? item?.bidder_name : "-"}
                      </td>
                      <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                        {item?.meta?.email ? item?.bidder_name : "-"}
                      </td>
                      <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                        {item?.bidding_amount ? item?.bidding_amount : "-"}
                      </td>
                      <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                        {item?.bid_timestamp
                          ? `${DateTime.fromMillis(
                              parseInt(item?.bid_timestamp)
                            )
                              .setZone("Asia/Kolkata")
                              .toFormat("hh:mm:ss a")} (IST)`
                          : "-"}
                        {/* {item?.created_at
                          ? DateTime.fromISO(item?.created_at).toFormat(
                              "hh:mm:ss a"
                            )
                          : "-"} */}
                      </td>
                      <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                        {item?.meta?.user_ip ? item?.meta?.user_ip : "-"}
                      </td>
                      <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                        {item?.reason ? item?.reason : "-"}
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const WinnerDetail = (props: any) => {
  return (
    <div className="my-8">
      <div className="text-base text-left mb-4">
        Table contains the information of the users who have participated in the
        e-auction process with their latest activity log
      </div>
      <div>
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50 w-full">
            <tr>
              <th
                scope="col"
                colSpan={6}
                className=" w-full table-cell py-3.5 pl-4 pr-3 text-sm border border-black font-semibold text-gray-900 sm:pl-6"
              >
                Users Logged History
              </th>
            </tr>
            <tr>
              <th
                scope="col"
                className="py-3.5 pl-4 pr-3 text-sm border border-black font-semibold text-gray-900 sm:pl-6"
              >
                Index
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Bidder Name
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Email
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Date
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Time
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                IP Address
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white max-h-80 overflow-auto">
            {props.biddersLoggedHistory &&
              props.biddersLoggedHistory.map((item: any, index: number) => {
                return (
                  <tr className="" key={index}>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {index + 1}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item?.bidder_name ? item?.bidder_name : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item?.meta?.email ? item?.meta?.email : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item?.bid_timestamp
                        ? DateTime.fromMillis(parseInt(item?.bid_timestamp))
                            .setZone("Asia/Kolkata")
                            .toFormat("DD")
                        : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item?.bid_timestamp
                        ? `${DateTime.fromMillis(parseInt(item?.bid_timestamp))
                            .setZone("Asia/Kolkata")
                            .toFormat("hh:mm:ss a")} (IST)`
                        : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item?.meta?.user_ip ? item?.meta?.user_ip : "-"}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      <div className="mt-8">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50 w-full">
            <tr>
              <th
                scope="col"
                colSpan={7}
                className=" w-full table-cell py-3.5 pl-4 pr-3 text-sm border border-black font-semibold text-gray-900 sm:pl-6"
              >
                Non-Participating Users if Any / Not Logged in Users if any
                during event
              </th>
            </tr>
            <tr>
              <th
                scope="col"
                className="py-3.5 pl-4 pr-3 text-sm border border-black font-semibold text-gray-900 sm:pl-6"
              >
                Index
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Bidder Name
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Email
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Status
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Details
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white max-h-80 overflow-auto">
            {props.nonParticiatedBidders &&
              props.nonParticiatedBidders.map((item: any, index: number) => {
                return (
                  <tr className="" key={index}>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {index + 1}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item?.meta?.name ? item?.meta?.name : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item?.email ? item?.email : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {item?.status ? item?.status : "-"}
                    </td>
                    <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                      {"Bidder didn't participate"}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      <div className="mt-8">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50 w-full">
            <tr>
              <th
                scope="col"
                colSpan={7}
                className=" w-full table-cell py-3.5 pl-4 pr-3 text-sm border border-black font-semibold text-gray-900 sm:pl-6"
              >
                Winning Bid Details
              </th>
            </tr>
            <tr>
              <th
                scope="col"
                className="py-3.5 pl-4 pr-3 text-sm border border-black font-semibold text-gray-900 sm:pl-6"
              >
                Index
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Bidder Name
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Email
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Bid
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                Time
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-sm border border-black font-semibold text-gray-900"
              >
                IP Address
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white max-h-80 overflow-auto">
            {props.bidHistory &&
              props.bidHistory.map((item: any, index: number) => {
                if (index === 0) {
                  return (
                    <tr className="" key={index}>
                      <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                        {1}
                      </td>
                      <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                        {item?.bidder_name ? item?.bidder_name : "-"}
                      </td>
                      <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                        {item?.meta?.email ? item?.meta?.email : "-"}
                      </td>
                      <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                        {item?.bidding_amount ? item?.bidding_amount : "-"}
                      </td>
                      <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                        {item?.bid_timestamp
                          ? `${DateTime.fromMillis(
                              parseInt(item?.bid_timestamp)
                            ).toFormat("hh:mm:ss a")} (IST)`
                          : "-"}
                      </td>
                      <td className="whitespace-nowrap text-center px-3 py-4 border border-black text-sm text-gray-500">
                        {item?.meta?.user_ip ? item?.meta?.user_ip : "-"}
                      </td>
                    </tr>
                  );
                }
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
const AnnouncementDetails = (props: any) => {
  return (
    <div className="mt-8">
      <div className="text-base text-left">
        This table displays the complete history of announcements made during
        the e-auction process.
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full table-fixed divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                colSpan={3}
                className="py-3.5 pl-4 pr-3 text-sm border border-black text-center font-semibold text-gray-900 sm:pl-6 text-left"
              >
                Details of Announcements
              </th>
            </tr>
            <tr>
              <th
                scope="col"
                className="w-1/12 px-3 py-3.5 text-sm border border-black font-semibold text-gray-900 text-center"
              >
                Index
              </th>
              <th
                scope="col"
                className="w-3/12 px-3 py-3.5 text-sm border border-black font-semibold text-gray-900 text-center"
              >
                Time
              </th>
              <th
                scope="col"
                className="w-8/12 px-3 py-3.5 text-sm border border-black font-semibold text-gray-900 text-center"
              >
                Announcement
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {props?.announcementHistory &&
              props?.announcementHistory?.length > 0 &&
              props?.announcementHistory?.map((item: any, index: number) => (
                <tr key={index}>
                  <td className="text-center px-3 py-4 border border-black text-sm text-gray-500">
                    {index + 1}
                  </td>
                  <td className="text-center px-3 py-4 border border-black text-sm text-gray-500">
                    {item?.created_at
                      ? `${DateTime.fromISO(item?.created_at)
                          .setZone("Asia/Kolkata")
                          .toFormat("dd-MM-yyyy hh:mm:ss a")} (IST)`
                      : "-"}
                  </td>
                  <td className="text-left px-3 py-4 border border-black text-center text-sm text-gray-500">
                    {item?.announcement || "-"}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Footer = (props: any) => {
  const user = useSelector((state: any) => {
    return state.bankerAuth.user;
  });

  return (
    <div className="my-8">
      <div className="text-base text-left mt-8">
        Report has been reviewed and cross verified with bank records by 2 bank
        officials.
      </div>
      <div className="flex justify-between mt-16">
        <div>Signature of Bank Official</div>
        <div>Signature of Bank Official 2</div>
      </div>
      <div className="mt-8">
        <div>Name:</div>
        <div>Employee ID:</div>
        <div>Date:</div>
      </div>
      <div className="text-center mt-12">
        This is a system generated report downloaded by{" "}
        <span className="font-semibold">
          {user && user?.attributes[0]?.value}
        </span>{" "}
        from auctionbazaar.com
      </div>
    </div>
  );
};

const Header = (props: any) => {
  const [bankLogo, setBankLogo] = useState(null);
  const bank = useSelector((state: any) => {
    return state.bankerAuth.bank;
  });

  useEffect(() => {
    if (bank?.logo?.key_path) {
      setBankLogo(bank.logo.key_path);
    }
  }, [bank]);

  return (
    <div className="w-full">
      <div className="flex justify-between w-full py-4 px-16">
        <div className="flex justify-center items-center">
          {bankLogo && (
            <img
              alt="header"
              className="w-full object-cover"
              src={`${process.env.NEXT_PUBLIC_IMAGIX_URL}/${bankLogo}`}
            />
          )}
        </div>
        <div className="flex justify-center">
          <LogoSvg width="200" height="100" />
        </div>
      </div>
      <div className="flex w-full ">
        <div className="basis-1/4 h-1 bg-blue-600"></div>
        <div className="basis-3/4 h-1 bg-gray-200"></div>
      </div>
    </div>
  );
};
